import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Link } from 'react-router-dom';
import { queueServices } from 'api/services';
import Template from 'components/Template/Template';
import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { ShowNotification } from 'helpers/showNotice';
import { NoticeStatus } from 'store/ducks/notice/types';
import { MainLoader } from 'components/Loader/Loader';
import * as types from 'api/services/queue/types';
import { AppState } from 'store/store';
import { useDispatch, useSelector } from 'react-redux';
import * as serviceActions from 'store/ducks/services/actions';
import './CreateQueuePage.sass';

export const CreateQueuePage: React.FC = () => {
  const [hasError, setHasError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [nameRu, setNameRu] = useState('');
  const [nameOz, setNameOz] = useState('');
  const [nameUz, setNameUz] = useState('');
  const [nameEn, setNameEn] = useState('')
  const [nameKaa, setNameKaa] = useState('')
  const [queueType, setQueueType] = useState('');
  const [selectedBranch, setSelectedBranch] = useState('');
  const [queueTypesData, setQueueTypesData] = useState<
    { value: types.QueueType; label: string }[]
  >([]);
  const history = useNavigate();
  const dispatch = useDispatch();
  const i18n = useSelector(
    (state: AppState) => state.i18n.data.pages.createQueuePage
  );
  const lang = useSelector((state: AppState) => state.i18n.key);
  const { branches } = useSelector((state: AppState) => state.services);

  useEffect(() => {
    setQueueTypesData([
      { label: i18n.practice, value: types.QueueType.PRACTICE },
      { label: i18n.theory, value: types.QueueType.THEORY },
    ]);
    getBranches();
  }, [lang]);

  const getBranches = () => {
    if (!branches.length) dispatch(serviceActions.getBranches());
  };

  const createQueue = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!nameRu.trim() || !nameUz.trim() || !nameOz.trim() || !nameEn.trim() || !nameKaa.trim()) {
      setHasError(true);
      setLoading(false);
      ShowNotification('Заполните поля');
      return;
    }
    const queueData: types.IQueue = {
      titleRu: nameRu,
      titleOz: nameOz,
      titleUz: nameUz,
      titleEn: nameEn,
      titleKaa: nameKaa,
      type: queueType as types.QueueType,
      branchId: Number(selectedBranch),
    };

    try {
      setLoading(true);
      await queueServices.createQueue(queueData);
      ShowNotification(i18n.queueCreated, NoticeStatus.SUCCESS);
      history(`/queue`);
    } catch {
      ShowNotification(i18n.tryAgainLater); 
    } finally {
      setLoading(false);
    }
  };

  return (
    <Template>
      <div className='create-queue-page'>
        <Typography variant='h3'>{i18n.createQueue}</Typography>
        <form className='create-queue-page__form' onSubmit={createQueue}>
          <Grid
            className='row'
            container
            item
            md={6}
            spacing={2}
            alignItems='flex-end'
          >
            <Grid item xs={12}>
              <TextField
                size='small'
                value={nameUz}
                onChange={(e) => setNameUz(e.target.value)}
                label={i18n.nameUz}
                autoComplete='off'
                variant='outlined'
                error={hasError && !nameUz.trim()}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                size='small'
                value={nameOz}
                onChange={(e) => setNameOz(e.target.value)}
                label={i18n.nameOz}
                autoComplete='off'
                variant='outlined'
                error={hasError && !nameOz.trim()}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                size='small'
                value={nameRu}
                onChange={(e) => setNameRu(e.target.value)}
                label={i18n.nameRu}
                autoComplete='off'
                variant='outlined'
                error={hasError && !nameRu.trim()}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                size='small'
                value={nameEn}
                onChange={(e) => setNameEn(e.target.value)}
                label={'Название англ'}
                autoComplete='off'
                variant='outlined'
                error={hasError && !nameEn.trim()}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                size='small'
                value={nameKaa}
                onChange={(e) => setNameKaa(e.target.value)}
                label={'Название каракалпакский'}
                autoComplete='off'
                variant='outlined'
                error={hasError && !nameKaa.trim()}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth variant='outlined' size='small'>
                <InputLabel id='role'>{i18n.choiceQueueType}</InputLabel>
                <Select
                  onChange={(e) => setQueueType(e.target.value as string)}
                  value={queueType || ''}
                  variant='outlined'
                  size='small'
                  labelId='role'
                  label={i18n.choiceQueueType}
                >
                  {queueTypesData.map((i) => (
                    <MenuItem key={i.value} value={i.value}>
                      {i.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              {!!branches.length && (
                <FormControl fullWidth variant='outlined' size='small'>
                  <InputLabel id='role'>{i18n.choiceBranch}</InputLabel>
                  <Select
                    onChange={(e) => setSelectedBranch(e.target.value)}
                    value={selectedBranch}
                    variant='outlined'
                    size='small'
                    labelId='role'
                    label={i18n.choiceBranch}
                  >
                    {branches.map((i) => (
                      <MenuItem key={i.id} value={i.id}>
                        {i[`name${lang}`]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Grid>
          </Grid>
          <Grid
            container
            item
            md={6}
            spacing={2}
            className='create-queue-page__button-wrap'
          >
            <Grid item xs={6}>
              <Link className='create-queue-page__button' to={`/queue`}>
                <Button
                  variant='contained'
                  color='inherit'
                  className='create-queue-page__button'
                  size='large'
                >
                  {i18n.back}
                </Button>
              </Link>
            </Grid>
            <Grid item xs={6}>
              <Button
                variant='contained'
                color='success'
                className='create-queue-page__button'
                type='submit'
                disabled={loading}
                size='large'
              >
                {loading ? <MainLoader /> : `${i18n.save}`}
              </Button>
            </Grid>
          </Grid>
        </form>
      </div>
    </Template>
  );
};
